import { createApi } from '@reduxjs/toolkit/query/react'
import { CREATE_THERAPIST, LIST_THERAPIST, REMOVE_THERAPIST, RESEND_ACCOUNT_ACTIVATION_MAIL, THERAPIST_AS_COLLABORATOR, UPDATE_THERAPIST, VIEW_THERAPIST } from "config/apiPaths"
import axiosBaseQuery from "utils/axios/axiosBaseQuery"
import { invalidateAPITags } from "utils/common";
import { utilityApi } from "./utility";
import { RTK_CACHE_EXPIRATION } from "config/constants";

export const therapistApi = createApi({
  reducerPath: 'therapistApi',
  baseQuery: axiosBaseQuery({
    baseUrl: "/therapist",
  }),
  tagTypes: ["Therapist"],
  keepUnusedDataFor: RTK_CACHE_EXPIRATION,
  endpoints: (builder) => ({
    // get Therapist list
    getTherapistsList: builder.query({
      query: ({ search = "", type = "", status = "", page = 1, limit = 100, discipline = "", designation = "", sort = { column: "createdAt", direction: "ASC" } }) => ({
        method: "PATCH",
        url: LIST_THERAPIST,
        data: {
          search,
          status: status?.value,
          discipline,
          designation,
          page,
          limit,
          sort,
          type: type?.value, 
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: ["Therapist"]
    }),

    // get Therapist data
    getTherapistData: builder.query({
      query: (id) => ({
        method: "GET",
        url: `${VIEW_THERAPIST}/${id}`
      }),
      transformResponse: (response) => {
        let data = response?.data || {};
        let therapistData = { ...data, ...(data?.therapist_detail || {}) }
        return therapistData;
      },
      providesTags: ["Therapist"]
    }),

    // create Therapist
    createTherapist: builder.mutation({
      query: (data) => ({
        method: "POST",
        url: CREATE_THERAPIST,
        data
      }),
      invalidatesTags: ["Therapist"],

      // Invalidates therapist lookup data in utility api
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        invalidateTherapistLookup(queryFulfilled, dispatch);
      },
    }),

    // update Therapist
    updateTherapist: builder.mutation({
      query: (data) => ({
        method: "PUT",
        url: `${UPDATE_THERAPIST}/${data.id}`,
        data: data.payload
      }),
      invalidatesTags: ["Therapist"],

      // Invalidates therapist lookup data in utility api
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        invalidateTherapistLookup(queryFulfilled, dispatch);
      },
    }),

    // delete Therapist
    deleteTherapist: builder.mutation({
      query: (id) => ({
        method: "DELETE",
        url: `${REMOVE_THERAPIST}/${id}`
      }),
      invalidatesTags: ["Therapist"],

      // Invalidates therapist lookup data in utility api
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        invalidateTherapistLookup(queryFulfilled, dispatch);
      },
    }),

    // resend account activation mail
    resendAccountActivation: builder.mutation({
      query: (id) => ({
        method: "PUT",
        url: `${RESEND_ACCOUNT_ACTIVATION_MAIL}/${id}`
      })
    }),

    therapistAsCollaborator: builder.mutation({
      query: ({ id, data }) => ({
        method: "PUT",
        url: `${THERAPIST_AS_COLLABORATOR}/${id}`,
        data
      }),
      invalidatesTags: ["Therapist"],

      // Invalidates therapist lookup data in utility api
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        invalidateTherapistLookup(queryFulfilled, dispatch);
      },
    }),


  }),
})

const invalidateTherapistLookup = (queryFulfilled, dispatch) => {
  invalidateAPITags(queryFulfilled, dispatch, utilityApi, ["TherapistLookup"]);
}

export const {
  useGetTherapistsListQuery,
  useLazyGetTherapistDataQuery,
  useCreateTherapistMutation,
  useGetTherapistDataQuery,
  useUpdateTherapistMutation,
  useDeleteTherapistMutation,
  useResendAccountActivationMutation,
  useTherapistAsCollaboratorMutation
} = therapistApi;